import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NumericInput from "@/components/NumericInput";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CURRENCY_SYMBOL } from "@/components/CurrencyChip";
import { toast } from "sonner";
import { Download, Upload, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { matchesAll } from "@/lib/trFold";

// Perakende Fiyatlandırma FAZ 2 — Toplu Fiyat Yönetimi (Excel export/import + toplu yüzde)
const FIELD_LABELS = {
  purchase_price: "Alış Fiyatı",
  purchase_currency: "Alış Para Birimi",
  purchase_discount_percent: "Alış İskonto Oranı %",
  retail_markup_percent: "Perakende Artış Oranı %",
  sale_price: "Satış Fiyatı",
  sale_currency: "Satış Para Birimi",
  sale_tax_rate: "Satış KDV %",
};

const fmtN = (n) => {
  const v = Number(n);
  if (n === null || n === undefined || n === "") return "—";
  if (!Number.isFinite(v)) return String(n);
  const r2 = Math.round(v * 100) / 100;
  const more = Math.abs(v - r2) > 1e-9;
  return v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: more ? 4 : 2 });
};

export default function BulkPricingDialog({ open, onOpenChange, defaultMarkup = 50, canEdit = false, onDone }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState("list"); // list | import | bulk
  const [busy, setBusy] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [bulkScope, setBulkScope] = useState("filtered");
  const [bulkOp, setBulkOp] = useState("set_markup");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkData, setBulkData] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { limit: 2000 } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setView("list"); setSelected(new Set()); setImportData(null); setBulkData(null); load(); } }, [open]);

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      if (category && (p.category || "") !== category) return false;
      return matchesAll(search, [p.name, p.product_code]);
    });
  }, [rows, search, category]);
  const categories = useMemo(() => [...new Set(rows.map((p) => p.category).filter(Boolean))].sort(), [rows]);

  const effRate = (p) => (p.retail_markup_percent === null || p.retail_markup_percent === undefined) ? defaultMarkup : Number(p.retail_markup_percent);
  const curMatch = (p) => (p.purchase_currency || "TRY").toUpperCase() === (p.sale_currency || "TRY").toUpperCase();
  // Net alış = liste alışı × (1 - alış iskonto / 100). İskonto yoksa (null/0) net = liste alışı.
  const netPurchase = (p) => {
    const purchase = Number(p.purchase_price);
    if (!Number.isFinite(purchase) || purchase <= 0) return null;
    const d = Number(p.purchase_discount_percent || 0);
    const dEff = Number.isFinite(d) && d > 0 ? d : 0;
    return purchase * (100 - dEff) / 100;
  };
  const suggested = (p) => {
    const net = netPurchase(p);
    if (!curMatch(p) || net === null || net <= 0) return null;
    return (net * (100 + effRate(p))) / 100; // yuvarlama YOK
  };

  const doExport = async () => {
    try {
      setBusy(true);
      const resp = await api.get("/products/bulk-pricing/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "fiyatlar.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Fiyat listesi Excel olarak indirildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doImportPreview = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/bulk-pricing/import-preview", fd);
      setImportFile(file); setImportData(data); setView("import");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doImportApply = async () => {
    if (!importFile) return;
    try {
      setBusy(true);
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await api.post("/products/bulk-pricing/import-apply", fd);
      toast.success(`${data.updated} ürünün fiyatı güncellendi`);
      setView("list"); setImportData(null); setImportFile(null);
      load(); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const bulkIds = () => {
    if (bulkScope === "all") return null;
    const src = bulkScope === "selected" ? rows.filter((p) => selected.has(p.id)) : filtered;
    return src.map((p) => p.id);
  };

  const doBulk = async (dryRun) => {
    try {
      setBusy(true);
      const ids = bulkIds();
      if (ids && ids.length === 0) { toast.error("Kapsamda ürün yok"); return; }
      const payload = {
        scope: bulkScope === "all" ? "all" : "selected",
        product_ids: ids || undefined,
        op: bulkOp,
        value: Number(bulkValue),
        dry_run: dryRun,
      };
      const { data } = await api.post("/products/bulk-pricing/bulk-update", payload);
      if (dryRun) { setBulkData(data); setView("bulk"); }
      else {
        toast.success(`${data.updated} ürün güncellendi`);
        setView("list"); setBulkData(null); setBulkValue("");
        load(); onDone?.();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleAll = () => {
    const ids = filtered.map((p) => p.id);
    const allIn = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    ids.forEach((id) => (allIn ? next.delete(id) : next.add(id)));
    setSelected(next);
  };

  const ChangesTable = ({ changes, truncated }) => (
    <div className="border border-slate-800 rounded-sm overflow-hidden">
      <div className="max-h-72 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-slate-800">
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alan</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Eski Değer</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yeni Değer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {changes.map((c, i) => (
              <TableRow key={i} className="border-slate-800" data-testid={`bp-change-row-${i}`}>
                <TableCell className="text-xs text-slate-300">{c.name}</TableCell>
                <TableCell className="text-xs text-slate-400">{FIELD_LABELS[c.field] || c.field}</TableCell>
                <TableCell className="text-right font-mono text-xs text-slate-400">{fmtN(c.old)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-cyan-300">{fmtN(c.new)}</TableCell>
              </TableRow>
            ))}
            {changes.length === 0 && (
              <TableRow className="border-slate-800"><TableCell colSpan={4} className="text-center text-xs text-slate-500 py-6">Değişiklik yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {truncated && <div className="text-[10px] text-slate-500 px-3 py-1 border-t border-slate-800">Liste ilk 500 değişiklikle sınırlı gösteriliyor.</div>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-7xl max-h-[92vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="text-slate-50 tracking-tight">Toplu Fiyat Yönetimi</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 pb-6 space-y-4">
          {view === "list" && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  data-testid="bp-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ara — ürün adı / kodu…"
                  className="bg-slate-950 border-slate-700 rounded-sm w-64"
                />
                <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
                  <SelectTrigger data-testid="bp-category-filter" className="bg-slate-950 border-slate-700 rounded-sm w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__all">Tüm kategoriler</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex-1" />
                <Button data-testid="bp-export-btn" onClick={doExport} disabled={busy} variant="outline" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold">
                  <Download className="w-4 h-4 mr-2" /> Excel İndir
                </Button>
                {canEdit && (
                  <>
                    <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="bp-import-input"
                      onChange={(e) => doImportPreview(e.target.files?.[0])} />
                    <Button data-testid="bp-import-btn" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold">
                      <Upload className="w-4 h-4 mr-2" /> Excel Yükle
                    </Button>
                  </>
                )}
              </div>

              {canEdit && (
                <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-3 flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Kapsam</Label>
                    <Select value={bulkScope} onValueChange={setBulkScope}>
                      <SelectTrigger data-testid="bp-scope-select" className="bg-slate-950 border-slate-700 rounded-sm w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                        <SelectItem value="all" data-testid="bp-scope-all">Tüm ürünler ({rows.length})</SelectItem>
                        <SelectItem value="filtered" data-testid="bp-scope-filtered">Filtrelenmiş liste ({filtered.length})</SelectItem>
                        <SelectItem value="selected" data-testid="bp-scope-selected">Seçili ürünler ({selected.size})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">İşlem</Label>
                    <Select value={bulkOp} onValueChange={setBulkOp}>
                      <SelectTrigger data-testid="bp-op-select" className="bg-slate-950 border-slate-700 rounded-sm w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                        <SelectItem value="set_markup" data-testid="bp-op-set-markup">Perakende artış oranını değiştir</SelectItem>
                        <SelectItem value="adjust_sale_percent" data-testid="bp-op-adjust-sale">Satış fiyatını yüzde değiştir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      {bulkOp === "set_markup" ? "Yeni Oran %" : "Değişim % (örn: 10 veya -5)"}
                    </Label>
                    <NumericInput
                      data-testid="bp-value-input"
                      step="0.1"
                      value={bulkValue}
                      onChange={(v) => setBulkValue(v)}
                      placeholder="0"
                      className="bg-slate-950 border-slate-700 rounded-sm font-mono w-36"
                    />
                  </div>
                  <Button
                    data-testid="bp-preview-btn"
                    onClick={() => doBulk(true)}
                    disabled={busy || bulkValue === "" || bulkValue === null}
                    className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Önizle"}
                  </Button>
                </div>
              )}

              <div className="border border-slate-800 rounded-sm overflow-hidden">
                <div className="max-h-[46vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="w-8">
                          <input type="checkbox" data-testid="bp-select-all"
                            checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))}
                            onChange={toggleAll} className="accent-blue-500" />
                        </TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün Kodu</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün Adı</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kategori</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alış</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">PB</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Oran %</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Önerilen</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Satış</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">PB</TableHead>
                        <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">KDV %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((p) => {
                        const sg = suggested(p);
                        return (
                          <TableRow key={p.id} className="border-slate-800" data-testid={`bp-row-${p.id}`}>
                            <TableCell>
                              <input type="checkbox" data-testid={`bp-select-${p.id}`}
                                checked={selected.has(p.id)}
                                onChange={() => {
                                  const next = new Set(selected);
                                  next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                                  setSelected(next);
                                }} className="accent-blue-500" />
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-400">{p.product_code || "—"}</TableCell>
                            <TableCell className="text-xs text-slate-200">{p.name}</TableCell>
                            <TableCell className="text-xs text-slate-400">{p.category || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-300">{fmtN(p.purchase_price)}</TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">{(p.purchase_currency || "TRY").toUpperCase()}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-300">
                              %{fmtN(effRate(p))}{(p.retail_markup_percent === null || p.retail_markup_percent === undefined) && <span className="text-slate-600" title="Tenant varsayılanı">*</span>}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-cyan-300" title={!curMatch(p) ? "Alış ve satış para birimleri farklı — öneri hesaplanmadı" : undefined}>
                              {sg !== null ? `${CURRENCY_SYMBOL[(p.sale_currency || "TRY").toUpperCase()] || "₺"}${fmtN(sg)}` : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-100">{fmtN(p.sale_price)}</TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">{(p.sale_currency || "TRY").toUpperCase()}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-400">%{fmtN(p.sale_tax_rate ?? 10)}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!loading && filtered.length === 0 && (
                        <TableRow className="border-slate-800"><TableCell colSpan={11} className="text-center text-xs text-slate-500 py-8">Ürün bulunamadı</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-[10px] text-slate-500 px-3 py-1 border-t border-slate-800 font-mono">
                  {filtered.length} ürün · {selected.size} seçili · * = tenant varsayılan oranı (%{fmtN(defaultMarkup)})
                </div>
              </div>
            </>
          )}

          {view === "import" && importData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="bp-import-summary">
                {[
                  ["Excel Satırı", importData.total_rows, "text-slate-200"],
                  ["Değişecek Ürün", importData.changed, "text-cyan-300"],
                  ["Değişmeyen", importData.unchanged, "text-slate-400"],
                  ["Hatalı", importData.errors, "text-rose-400"],
                  ["Bulunamayan Ürün", importData.not_found, "text-amber-400"],
                ].map(([label, val, cls]) => (
                  <div key={label} className="rounded-sm border border-slate-800 bg-slate-950/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
                    <div className={`text-xl font-mono font-bold ${cls}`} data-testid={`bp-sum-${label}`}>{val}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Değişiklikler</div>
              <ChangesTable changes={importData.changes || []} truncated={importData.changes_truncated} />
              {(importData.error_rows || []).length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-rose-400 font-mono flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Hatalı / Bulunamayan Satırlar
                  </div>
                  <div className="border border-rose-900/40 rounded-sm overflow-hidden" data-testid="bp-import-errors">
                    <div className="max-h-40 overflow-y-auto">
                      <Table>
                        <TableBody>
                          {importData.error_rows.map((e, i) => (
                            <TableRow key={i} className="border-slate-800">
                              <TableCell className="font-mono text-xs text-slate-500 w-24">Satır {e.row}</TableCell>
                              <TableCell className="font-mono text-xs text-slate-400">{e.product_id || "—"}</TableCell>
                              <TableCell className="text-xs text-rose-300">{e.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between gap-2 pt-2">
                <Button data-testid="bp-import-back-btn" variant="outline" onClick={() => { setView("list"); setImportData(null); }} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Geri
                </Button>
                <Button
                  data-testid="bp-apply-import-btn"
                  onClick={doImportApply}
                  disabled={busy || !importData.changed}
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Fiyatları Güncelle (${importData.changed} ürün)`}
                </Button>
              </div>
            </>
          )}

          {view === "bulk" && bulkData && (
            <>
              <div className="rounded-sm border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs font-mono text-cyan-300" data-testid="bp-bulk-summary">
                Kapsam: {bulkData.matched} ürün · Değişecek: {bulkData.changed} ürün ·
                İşlem: {bulkOp === "set_markup" ? `Artış oranı → %${bulkValue}` : `Satış fiyatı ${Number(bulkValue) >= 0 ? "+" : ""}%${bulkValue}`}
              </div>
              <ChangesTable changes={bulkData.changes || []} truncated={bulkData.changes_truncated} />
              <div className="flex items-center justify-between gap-2 pt-2">
                <Button data-testid="bp-bulk-back-btn" variant="outline" onClick={() => { setView("list"); setBulkData(null); }} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Geri
                </Button>
                <Button
                  data-testid="bp-apply-bulk-btn"
                  onClick={() => doBulk(false)}
                  disabled={busy || !bulkData.changed}
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Onayla & Uygula (${bulkData.changed} ürün)`}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
